import { useSyncExternalStore } from 'react';

import { PHASE1_PAGES, type Phase1PageId } from './phase1Paths';

/**
 * AR-445 — the Bookmarks panel's list.
 *
 * Phase 1 has no bookmarks API, so the list lives in this browser — the same
 * per-device convenience the sidebar's collapsed state already is. It holds page
 * ids, never paths, so a page that moves keeps its bookmark, and an id the
 * registry no longer knows is dropped on read rather than rendered as a dead row.
 */

const KEY = 'aivia-phase1-bookmarks';
const listeners = new Set<() => void>();

function read(): Phase1PageId[] {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? '[]');
    return Array.isArray(raw) ? raw.filter((id): id is Phase1PageId => id in PHASE1_PAGES) : [];
  } catch {
    return [];
  }
}

let snapshot: Phase1PageId[] = typeof window === 'undefined' ? [] : read();

function write(next: Phase1PageId[]) {
  snapshot = next;
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* storage blocked — the list still works for this session */
  }
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function usePhase1Bookmarks() {
  const ids = useSyncExternalStore(subscribe, () => snapshot, () => snapshot);
  return {
    ids,
    has: (id: Phase1PageId) => ids.includes(id),
    toggle: (id: Phase1PageId) =>
      write(ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]),
  };
}
